import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as CommentIcon } from '../../../assets/svg/comment.svg';
import { ReactComponent as CoursesIcon } from '../../../assets/svg/courses.svg';
import { ReactComponent as GroupsIcon } from '../../../assets/svg/groups.svg';
// import { ReactComponent as FavoriteIcon } from '../../../assets/svg/favorite.svg';
import { ReactComponent as HeartIcon } from '../../../assets/svg/heart.svg';
import { ReactComponent as HomeIcon } from '../../../assets/svg/home.svg';
import { ReactComponent as PlayIcon } from '../../../assets/svg/play.svg';
import { ReactComponent as SchoolIcon } from '../../../assets/svg/school.svg';
import { ReactComponent as StacksIcon } from '../../../assets/svg/stacks.svg';
import { ReactComponent as TrophyIcon } from '../../../assets/svg/trophy.svg';
import { useAPI } from '../../../contexts/APIContext';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { canAccessFeature, canEnterEcm, canSeeShop, CloudFunctions, getFunctionsUrl } from '../../utils/utils';
import { Feedback } from '../Feedback/Feedback';
import styles from './Sidebar.module.scss';

const SidebarComponent = () => {
  const { staticData, subscription, userDataFmt, mostViewsVideos, keepWatching, userCompanyData, currentUser } = useFirebase();
  const { currentLanguage } = useCurrentLanguage();
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();

  const { checkMyLives } = useAPI();
  const [livesAvailable, setLivesAvailable] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [activeBrowseSection, setActiveBrowseSection] = useState<string | null>(null);
  const [pendingBrowseSection, setPendingBrowseSection] = useState<string | null>(null);
  const showEcm =
    !!userDataFmt &&
    !!currentLanguage &&
    !!Object.keys(staticData || {}).length &&
    canAccessFeature('ECM', currentLanguage, staticData, userDataFmt) &&
    canEnterEcm(subscription, userDataFmt) &&
    userDataFmt.profile?.formData?.categoriaProfessionale !== 'aso' &&
    currentLanguage === 'it';

  useEffect(() => {
    checkMyLives().then(({ check }) => setLivesAvailable(check));
  }, []);

  useEffect(() => {
    if (!pathname.startsWith('/browse')) {
      setActiveBrowseSection(null);
      setPendingBrowseSection(null);
      return;
    }

    const sections = [
      ['continue-watching', document.getElementById('continue-watching')],
      ['top-of-week', document.getElementById('top-of-week')],
    ].filter(([, element]) => element) as [string, HTMLElement][];
    if (!sections.length) {
      setActiveBrowseSection(null);
      return;
    }

    const updateActiveSection = () => {
      const viewportCenter = window.innerHeight / 2;
      const centeredSection = sections.find(([, element]) => {
        const { top, bottom } = element.getBoundingClientRect();
        return top <= viewportCenter && bottom >= viewportCenter;
      });

      if (centeredSection && pendingBrowseSection && centeredSection[0] !== pendingBrowseSection) return;

      if (centeredSection) {
        setActiveBrowseSection(centeredSection[0]);
        setPendingBrowseSection(null);
      }
    };

    let frame: number | null = null;
    const handleScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updateActiveSection();
      });
    };

    updateActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [pathname, userDataFmt, keepWatching.length, mostViewsVideos.length, staticData.SHOW_MOST_VIEWS, pendingBrowseSection]);

  useEffect(() => {
    if (!pathname.startsWith('/browse')) return;

    const section = hash.slice(1);
    if (!['continue-watching', 'top-of-week'].includes(section)) {
      setPendingBrowseSection(null);
      return;
    }

    setActiveBrowseSection(section);
    setPendingBrowseSection(section);
  }, [pathname, hash]);

  const items = [
    { label: t('labels.home'), url: '/browse', icon: HomeIcon },
    ...(keepWatching.length ? [{ label: t('labels.continuaAGuardare'), url: '/browse#continue-watching', icon: PlayIcon }] : []),
    ...(staticData.SHOW_MOST_VIEWS && mostViewsVideos.length ? [{ label: t('labels.topWeek'), url: '/browse#top-of-week', icon: TrophyIcon }] : []),
    { label: t('categorie.labels.titolo'), url: '/categorie', icon: StacksIcon },
    { label: t('labels.videoSalvati'), url: '/list', icon: HeartIcon },
  ];

  const isActive = (item: { label: string; url: string }) => {
    if (item.url === '/browse#continue-watching') return pathname.startsWith('/browse') && activeBrowseSection === 'continue-watching';
    if (item.url === '/browse#top-of-week') return pathname.startsWith('/browse') && activeBrowseSection === 'top-of-week';
    if (item.url === '/browse') return pathname.startsWith('/browse') && !activeBrowseSection;
    if (item.url === '/list') return pathname.startsWith('/list');
    return item.url === '/categorie' && pathname.startsWith('/categorie');
  };

  const scrollBrowseNavigation = (url: string) => {
    if (!url.startsWith('/browse')) return;

    const targetId = url.split('#')[1];
    if (targetId) {
      setActiveBrowseSection(targetId);
      setPendingBrowseSection(targetId);

      if (!pathname.startsWith('/browse')) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      const topbarHeight = document.querySelector('header')?.getBoundingClientRect().height || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - topbarHeight;
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    if (!pathname.startsWith('/browse')) return;

    setActiveBrowseSection(null);
    setPendingBrowseSection(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <aside className={styles.sidebar} aria-label={t('labels.desktopNavigation')}>
      <nav>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.label}
              to={item.url}
              className={`${styles.item} ${isActive(item) ? styles.active : ''}`}
              title={item.label}
              onClick={() => scrollBrowseNavigation(item.url)}
            >
              <Icon className={item.url === '/browse#continue-watching' ? styles.continueIcon : undefined} aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          );
        })}

        <div className={styles.divider} />

        {canAccessFeature('SHOP', currentLanguage, staticData, userDataFmt) && canSeeShop(subscription, userDataFmt, staticData, currentLanguage) && (
          <Link to={'/shop'} className={styles.item} title="Tootor ECM">
            <SchoolIcon aria-hidden="true" />
            <span>{t('buyEcm.labels.tootorECM')}</span>
          </Link>
        )}
        {!!staticData.SHOW_TOOTORS_LIST && (
          <Link to={'/tootors'} className={`${styles.item} ${pathname.startsWith('/tootors') ? styles.active : ''}`} title={t('labels.tootors')}>
            <GroupsIcon aria-hidden="true" />
            <span>{t('labels.tootors')}</span>
          </Link>
        )}
        {userDataFmt?.profile?.formData?.categoriaProfessionale === 'aso' && currentLanguage?.toLowerCase() === 'it' && (
          <Link to={'/courses'} className={`${styles.item} ${pathname.startsWith('/courses') ? styles.active : ''}`} title={t('labels.corsi')}>
            <CoursesIcon aria-hidden="true" />
            <span>{t('labels.corsi')}</span>
          </Link>
        )}
        {!!staticData.ENABLE_COMPANIES &&
          !!userCompanyData?.enabled &&
          !!userCompanyData.id && (
            <Link
              to={`/company/${userCompanyData.id}/courses`}
              className={`${styles.item} ${pathname.startsWith(`/company/${userCompanyData.id}/courses`) ? styles.active : ''}`}
              title={userCompanyData.name}
            >
              <CoursesIcon aria-hidden="true" />
              <span>{userCompanyData.name}</span>
            </Link>
          )}
        {showEcm && (
          <button
            type="button"
            className={styles.item}
            onClick={async () => {
              const url = new URL(getFunctionsUrl(CloudFunctions.GetFadAuthenticatedUrl));
              url.searchParams.append('uid', currentUser.uid);
              url.searchParams.append('token', await currentUser.getIdToken(true));
              url.searchParams.append('email', currentUser.email);
              window.location.href = url.toString();
            }}
          >
            <CoursesIcon aria-hidden="true" />
            <span>{t('shop.ECMShopWarningGoECM')}</span>
          </button>
        )}
        {livesAvailable && (
          <Link to={'/live'} className={styles.item} title={t('live.labels.navButton')}>
            <SchoolIcon aria-hidden="true" />
            <span>{t('live.labels.navButton')}</span>
          </Link>
        )}
        {!!staticData.SHOW_FEEDBACK_MENU && (
          <button type="button" className={styles.item} onClick={() => setFeedbackOpen(true)} title={t('labels.lasciaFeedback')}>
            <CommentIcon aria-hidden="true" />
            <span>{t('labels.lasciaFeedback')}</span>
          </button>
        )}
      </nav>

      <Feedback show={feedbackOpen} onHide={() => setFeedbackOpen(false)} />
    </aside>
  );
};

export { SidebarComponent as Sidebar };
